import { fromQuoteSheet, IQuoteSheet, useQuoteService } from 'features/BookingFlow'
import { ILocationModelJSON, NetworkService } from 'features/Directory'
import { compact, flatMap, flow, keyBy, uniq } from 'lodash-es'
import { inject } from 'mobx-react'
import { FC, useCallback, useEffect, useMemo, useReducer } from 'react'
import { RootStore } from 'service/RootStore'
import { logError } from 'utils/logger'

import { IBookingTemplate, IBookingTemplateWithLocation } from '../BookingTemplate'
import { BookingTemplateRequestDialogSwitch as BookingTemplateRequestDialog } from '../BookingTemplateRequest/BookingTemplateRequestDialogRoot'
import { BookingTemplateService } from '../BookingTemplateService'
import { NewQuoteButton, NewQuoteButtonProps } from './NewQuoteButton'
import { NewQuoteButtonContext, newQuoteButtonInitialState, newQuoteButtonReducer } from './NewQuoteButtonContext'

const MAX_DISPLAYED_ITEMS = 5

interface NewQuoteButtonRootInject {
  bookingTemplateService: BookingTemplateService
  networkService: NetworkService
}

interface NewQuoteButtonRootProps extends NewQuoteButtonProps, Partial<NewQuoteButtonRootInject> {}

const LeanNewQuoteButtonRoot: FC<NewQuoteButtonRootProps> = ({ bookingTemplateService, networkService, ...props }) => {
  const [state, dispatch] = useReducer(newQuoteButtonReducer, newQuoteButtonInitialState)
  const { isRequestDialogOpen, selectedTemplate } = state
  const quoteService = useQuoteService()

  useEffect(() => {
    load()
  }, [])

  const loadRecentQuotes = useCallback(async () => {
    const recentQuotes = await quoteService.recentSearches()

    dispatch({ type: 'setRecentQuotes', recentQuotes })
  }, [quoteService])

  const loadBookingTemplates = useCallback(async () => {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    const { templates } = await bookingTemplateService.getBookingTemplates({
      from: 0,
      size: MAX_DISPLAYED_ITEMS,
    })
    const locationIds: string[] = flow(
      (_templates: IBookingTemplate[]) => flatMap(_templates, getTemplateLocationIds),
      compact,
      uniq
    )(templates)

    if (locationIds.length === 0) {
      dispatch({ type: 'setBookingTemplates', bookingTemplates: templates })

      return
    }

    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    const locations = await networkService.locationsService.findByIds(locationIds)
    const templatesWithLocations = addLocationsToTemplates(locations, templates)

    dispatch({ type: 'setBookingTemplates', bookingTemplates: templatesWithLocations })
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  }, [bookingTemplateService, networkService.locationsService])

  const load = useCallback(async () => {
    dispatch({ type: 'setIsLoading', isLoading: true })
    try {
      await Promise.all([loadRecentQuotes(), loadBookingTemplates()])
    } catch (e) {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      logError(e)
    } finally {
      dispatch({ type: 'setIsLoading', isLoading: false })
    }
  }, [loadBookingTemplates, loadRecentQuotes])

  const value = useMemo(() => {
    const requestQuote = (quoteSheet: IQuoteSheet) => {
      quoteService.search(fromQuoteSheet(quoteSheet))
    }

    return {
      ...state,
      dispatch,
      reload: load,
      requestQuote,
    }
  }, [state, load, quoteService])

  return (
    <>
      <NewQuoteButtonContext.Provider value={value}>
        <NewQuoteButton {...props} />
      </NewQuoteButtonContext.Provider>
      <BookingTemplateRequestDialog
        isOpen={isRequestDialogOpen}
        onClose={() => dispatch({ type: 'closeRequestDialog' })}
        template={selectedTemplate}
        // @ts-expect-error TS(2322) FIXME: Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
        templateId={selectedTemplate ? selectedTemplate._id : null}
      />
    </>
  )
}

const injector = inject(
  (ctx: RootStore): NewQuoteButtonRootInject => ({
    bookingTemplateService: ctx.bookingTemplateService,
    networkService: ctx.networkService,
  })
)

export const NewQuoteButtonRoot = injector(LeanNewQuoteButtonRoot)

const getTemplateLocationIds = (template: IBookingTemplate) => [
  template.originLocationId,
  template.destinationLocationId,
]

const addLocationsToTemplates = (
  locations: ILocationModelJSON[],
  templates: IBookingTemplate[]
): IBookingTemplateWithLocation[] => {
  const locationsMap: Record<string, ILocationModelJSON> = keyBy(locations, (location) => location._id)

  return templates.map((template) => {
    const { originLocationId, destinationLocationId } = template

    return {
      ...template,
      ...(originLocationId ? { originLocation: locationsMap[originLocationId] } : {}),
      ...(destinationLocationId ? { destinationLocation: locationsMap[destinationLocationId] } : {}),
    }
  })
}
